/**
 * IonTierLimitsMatrix — per-tier Ion (AI chat) limits as a Budgets × Tiers grid.
 * =============================================================================
 * Replaces the four side-by-side IonTierLimitsPanel columns in the
 * AI-Management tab (their varying-length headings never lined up). Rows are the
 * three USD budgets (Daily / Weekly / Monthly), columns are the tiers
 * (PRO / DEMO / batemo / UNLIMITED). Aligned by construction; the field
 * explanation lives once on the row label instead of repeated per column.
 *
 * Same backend contract as IonTierLimitsPanel (which stays for the single-tier
 * DemoTab view): GET /admin/chat-management/tier-limits returns per-tier values
 * + a per-tier `overridden` map + server-computed `*_tokens`; PUT sends
 * `{ tier, [field]: value|null }` (null clears the override → env default).
 *
 * UNLIMITED is a runaway-abuse brake with only a daily budget — its weekly and
 * monthly cells render as "—".
 */
import { useEffect, useState } from 'preact/hooks';
import { getChatTierLimits, updateChatTierLimits } from '../../services/adminService.js';

// Columns (display order). `sub` is a small qualifier under the tier name.
const TIERS = [
  { key: 'pro', label: 'PRO', fields: ['daily_usd', 'weekly_usd', 'monthly_usd'] },
  { key: 'demo', label: 'DEMO', fields: ['daily_usd', 'weekly_usd', 'monthly_usd'] },
  { key: 'batemo', label: 'batemo', sub: 'internal', fields: ['daily_usd', 'weekly_usd', 'monthly_usd'] },
  { key: 'unlimited', label: 'Unlimited', sub: 'runaway brake', fields: ['daily_usd'] },
];

// Rows (the three budgets). `help` shows once on the row label.
const ROWS = [
  { key: 'daily_usd', tokenKey: 'daily_tokens', label: 'Daily budget ($)', min: 0, max: 10000,
    help: 'Soft daily USD budget across a user’s Ion usage before requests are blocked.' },
  { key: 'weekly_usd', tokenKey: 'weekly_tokens', label: 'Weekly budget ($)', min: 0, max: 100000,
    help: 'Soft weekly USD budget across a user’s Ion usage before requests are blocked.' },
  { key: 'monthly_usd', tokenKey: 'monthly_tokens', label: 'Monthly budget ($)', min: 0, max: 100000,
    help: 'Plan allotment — admin-only for every tier (users self-service daily/weekly within it, never this). For DEMO it is a one-time total-for-the-account ceiling, not a monthly reset.' },
];

export function IonTierLimitsMatrix() {
  const [limits, setLimits] = useState(null);
  const [drafts, setDrafts] = useState({}); // { [tier]: { [field]: string } }
  const [loading, setLoading] = useState(true);
  const [savingCell, setSavingCell] = useState(null); // `${tier}:${field}`
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyLimits(data) {
    setLimits(data);
    const seeded = {};
    TIERS.forEach((t) => {
      const vals = data?.[t.key] || {};
      seeded[t.key] = {};
      t.fields.forEach((f) => {
        seeded[t.key][f] = vals[f] != null ? String(vals[f]) : '';
      });
    });
    setDrafts(seeded);
  }

  async function load() {
    setLoading(true);
    setError('');
    try {
      applyLimits(await getChatTierLimits());
    } catch (err) {
      console.error('[IonTierLimitsMatrix] Load error:', err);
      setError(err?.message || 'Failed to load Ion tier limits');
    } finally {
      setLoading(false);
    }
  }

  function flashSuccess(msg) {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 4000);
  }

  function parseValue(row, raw) {
    const trimmed = String(raw ?? '').trim();
    if (trimmed === '') return { error: 'Enter a value.' };
    const value = parseFloat(trimmed);
    if (!Number.isFinite(value) || value < row.min || value > row.max) {
      return { error: `Enter a number between ${row.min} and ${row.max}.` };
    }
    return { value };
  }

  async function saveCell(tier, row) {
    const parsed = parseValue(row, drafts[tier]?.[row.key]);
    if (parsed.error) {
      setError(parsed.error);
      return;
    }
    setError('');
    setSavingCell(`${tier}:${row.key}`);
    try {
      applyLimits(await updateChatTierLimits({ tier, [row.key]: parsed.value }));
      flashSuccess('Saved. Applies to new requests within about a minute.');
    } catch (err) {
      console.error('[IonTierLimitsMatrix] Save error:', err);
      setError(err?.message || 'Failed to save');
    } finally {
      setSavingCell(null);
    }
  }

  async function resetCell(tier, row) {
    setError('');
    setSavingCell(`${tier}:${row.key}`);
    try {
      // null clears the DB override → reverts to the env default.
      applyLimits(await updateChatTierLimits({ tier, [row.key]: null }));
      flashSuccess('Reverted to default. Applies within about a minute.');
    } catch (err) {
      console.error('[IonTierLimitsMatrix] Reset error:', err);
      setError(err?.message || 'Failed to reset');
    } finally {
      setSavingCell(null);
    }
  }

  return (
    <div className="ion-matrix">
      <div className="admin-section-header ion-matrix-header">
        <h2>Tier limits</h2>
        <button className="admin-btn admin-btn--secondary" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>

      {success && <div className="admin-alert admin-alert--success">{success}</div>}
      {error && <div className="admin-alert admin-alert--error">{error}</div>}

      {loading ? (
        <div className="admin-loading">
          <div className="admin-spinner"></div>
          <p>Loading Ion limits…</p>
        </div>
      ) : limits ? (
        <div className="ion-matrix-scroll">
          <div className="ion-matrix-grid">
            {/* Header row: corner + tier names */}
            <div className="ion-matrix-corner" />
            {TIERS.map((t) => (
              <div className="ion-matrix-tier-head" key={t.key}>
                <span className="ion-matrix-tier-name">{t.label}</span>
                {t.sub && <span className="ion-matrix-tier-sub">{t.sub}</span>}
              </div>
            ))}

            {/* One row per budget */}
            {ROWS.map((row) => (
              <MatrixRow
                key={row.key}
                row={row}
                limits={limits}
                drafts={drafts}
                setDrafts={setDrafts}
                savingCell={savingCell}
                onSave={saveCell}
                onReset={resetCell}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MatrixRow({ row, limits, drafts, setDrafts, savingCell, onSave, onReset }) {
  return (
    <>
      <div className="ion-matrix-row-label">
        <span className="ion-matrix-row-title">{row.label}</span>
        <p className="ion-matrix-row-help">{row.help}</p>
      </div>
      {TIERS.map((t) => {
        if (!t.fields.includes(row.key)) {
          return (
            <div className="ion-matrix-cell ion-matrix-cell--na" key={t.key} aria-hidden="true">
              —
            </div>
          );
        }
        const tierVals = limits?.[t.key] || {};
        const isOverride = limits?.overridden?.[t.key]?.[row.key] === true;
        const draftVal = drafts[t.key]?.[row.key] ?? '';
        const dirty = String(draftVal) !== String(tierVals[row.key] ?? '');
        const busy = savingCell === `${t.key}:${row.key}`;
        const tokenHint = tierVals[row.tokenKey];
        return (
          <div className="ion-matrix-cell" key={t.key}>
            <div className="ion-matrix-cell-input">
              <input
                className="admin-demo-input"
                type="number"
                min={row.min}
                max={row.max}
                step={0.01}
                value={draftVal}
                disabled={busy}
                aria-label={`${t.label} ${row.label}`}
                onInput={(e) =>
                  setDrafts((d) => ({ ...d, [t.key]: { ...d[t.key], [row.key]: e.target.value } }))
                }
              />
              <button
                className="admin-btn admin-btn--primary ion-matrix-save"
                onClick={() => onSave(t.key, row)}
                disabled={busy || !dirty}
              >
                {busy ? '…' : 'Save'}
              </button>
            </div>
            <div className="ion-matrix-cell-meta">
              <span className={`admin-demo-badge${isOverride ? ' admin-demo-badge--override' : ''}`}>
                {isOverride ? 'overridden' : 'default'}
              </span>
              {tokenHint != null && <span className="ion-matrix-tokens">≈ {tokenHint} tok</span>}
              {isOverride && (
                <button
                  className="ion-matrix-reset"
                  onClick={() => onReset(t.key, row)}
                  disabled={busy}
                  title="Clear the override and revert to the environment default"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
